/**
 * TraceList - Paginated trace list with dual-line card rows
 */

import { useState } from 'react';
import { Search, ChevronLeft, ChevronRight, AlertCircle, Cpu, Wrench, User } from 'lucide-react';
import { formatDuration, formatTokens } from './spanConstants';
import type { TracePublic, TraceListParams } from '@/types/traces';
import { useTraceList } from '../hooks/useTraces';
import { useI18n } from '@/i18n';

interface TraceListProps {
  onSelectTrace: (traceId: string) => void;
  selectedTraceId: string | null;
}

export function TraceList({ onSelectTrace, selectedTraceId }: TraceListProps) {
  const { messages } = useI18n();
  const t = messages.tracesPage;
  // Default to showing only chat_request traces (which carry user_id/topic_id context)
  const [showAllTraces, setShowAllTraces] = useState(false);
  const [params, setParams] = useState<TraceListParams>({
    page: 1,
    size: 20,
    truncate: true,
    name: 'chat_request',
  });
  const [searchInput, setSearchInput] = useState('');

  const { data, isLoading, isError, error } = useTraceList(params);

  const handleSearch = () => {
    setParams(prev => ({ ...prev, page: 1, name: searchInput || (showAllTraces ? undefined : 'chat_request') }));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const pageSize = params.size ?? 20;
  const totalPages = data ? Math.ceil(data.total / pageSize) : 0;

  return (
    <div className="flex flex-col h-full">
      {/* Search bar */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t.searchPlaceholder}
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
          >
            {t.search}
          </button>
        </div>
        {/* Filter toggle */}
        <div className="flex items-center gap-1 mt-2">
          <button
            onClick={() => { setShowAllTraces(false); setSearchInput(''); setParams(prev => ({ ...prev, page: 1, name: 'chat_request' })); }}
            className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
              !showAllTraces ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {t.filterChatOnly}
          </button>
          <button
            onClick={() => { setShowAllTraces(true); setSearchInput(''); setParams(prev => ({ ...prev, page: 1, name: undefined })); }}
            className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
              showAllTraces ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {t.filterAll}
          </button>
        </div>
      </div>

      {/* Card list */}
      <div className="flex-1 overflow-auto">
        {isLoading && (
          <div className="flex items-center justify-center h-32 text-gray-500">{t.loading}</div>
        )}
        {isError && (
          <div className="flex items-center justify-center h-32 text-red-500">
            <AlertCircle className="w-4 h-4 mr-2" />
            {(error as Error)?.message || t.loadFailed}
          </div>
        )}
        {data && (
          <div className="divide-y divide-gray-100">
            {data.content.map(trace => (
              <TraceRow
                key={trace.id}
                trace={trace}
                isSelected={trace.id === selectedTraceId}
                onClick={() => onSelectTrace(trace.id)}
              />
            ))}
            {data.content.length === 0 && (
              <div className="px-4 py-8 text-center text-gray-500">
                {t.noData}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pagination */}
      {data && data.total > 0 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 text-sm">
          <span className="text-gray-600">
            {t.pagination.replace('{total}', String(data.total)).replace('{page}', String(data.page)).replace('{pages}', String(totalPages))}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setParams(prev => ({ ...prev, page: (prev.page ?? 1) - 1 }))}
              disabled={data.page <= 1}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => setParams(prev => ({ ...prev, page: (prev.page ?? 1) + 1 }))}
              disabled={data.page >= totalPages}
              className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function TraceRow({
  trace,
  isSelected,
  onClick,
}: {
  trace: TracePublic;
  isSelected: boolean;
  onClick: () => void;
}) {
  const { messages } = useI18n();
  const t = messages.tracesPage;
  const startTime = new Date(trace.start_time);
  const hasError = !!trace.error_info;

  // Safe metadata access (metadata is typed as unknown)
  const meta = trace.metadata as Record<string, any> | null | undefined;
  // Also try trace.input for OTEL root spans (flat keys like "chat.agent_type")
  const input = trace.input as Record<string, any> | null | undefined;
  const agentType = meta?.chat?.agent_type as string | undefined
    || meta?.agent_type as string | undefined
    || input?.['chat.agent_type'] as string | undefined;
  const model = trace.providers?.[0]
    || (meta?.chat?.model as string | undefined)
    || (input?.['chat.model'] as string | undefined);
  // Extract user_id: OTEL root span input > LiteLLM metadata (flat or nested) > thread_id
  const userId = input?.['chat.user_id'] as string | undefined
    || meta?.user_id as string | undefined
    || meta?.chat?.user_id as string | undefined
    || meta?.user as string | undefined;
  const topicId = input?.['chat.topic_id'] as string | undefined
    || meta?.topic_id as string | undefined
    || meta?.chat?.topic_id as string | undefined
    || trace.thread_id as string | undefined;

  // Pipeline preview icons
  const pipelineIcons: { type: string; Icon: typeof Cpu }[] = [];
  if (trace.llm_span_count && trace.llm_span_count > 0) {
    pipelineIcons.push({ type: 'llm', Icon: Cpu });
  }
  if (trace.has_tool_spans) {
    pipelineIcons.push({ type: 'tool', Icon: Wrench });
  }

  return (
    <div
      onClick={onClick}
      className={`px-4 py-3 cursor-pointer transition-colors border-l-2 ${
        isSelected
          ? 'bg-blue-50 border-l-blue-500'
          : hasError
            ? 'bg-red-50/40 border-l-red-400 hover:bg-red-50/60'
            : 'border-l-transparent hover:bg-gray-50'
      }`}
    >
      {/* Row 1: Status + Name + Time */}
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${hasError ? 'bg-red-500' : 'bg-green-500'}`} />
        <span className="font-mono text-xs truncate flex-1 text-gray-900">
          {trace.name || trace.id.slice(0, 12)}
        </span>
        <span className="text-[11px] text-gray-400 whitespace-nowrap flex-shrink-0">
          {startTime.toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </span>
      </div>

      {/* Row 2: Metadata badges + Pipeline + Metrics */}
      <div className="flex items-center gap-2 mt-1.5 text-[11px]">
        {/* Agent type badge */}
        {agentType && (
          <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded font-medium truncate max-w-[100px]">
            {agentType}
          </span>
        )}

        {/* Model tag */}
        {model ? (
          <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded truncate max-w-[120px]">
            {model}
          </span>
        ) : null}

        {/* User ID badge */}
        {userId && (
          <span className="flex items-center gap-0.5 px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded truncate max-w-[90px]" title={userId}>
            <User className="w-2.5 h-2.5 flex-shrink-0" />
            {userId.length > 8 ? userId.slice(0, 8) : userId}
          </span>
        )}

        {/* Topic ID badge */}
        {topicId && (
          <span className="px-1.5 py-0.5 bg-green-50 text-green-700 rounded truncate max-w-[90px]" title={topicId}>
            {topicId.length > 8 ? topicId.slice(0, 8) : topicId}
          </span>
        )}

        {/* Pipeline preview */}
        {pipelineIcons.length > 0 ? (
          <span className="flex items-center gap-0.5 text-gray-400">
            {pipelineIcons.map((p, i) => (
              <span key={p.type} className="flex items-center gap-0.5">
                {i > 0 && <span className="text-[9px]">&rarr;</span>}
                <p.Icon className="w-3 h-3" />
              </span>
            ))}
            {trace.span_count != null && (
              <span className="text-gray-400 ml-0.5">({trace.span_count})</span>
            )}
          </span>
        ) : trace.span_count != null ? (
          <span className="text-gray-400">
            {t.spanCount.replace('{count}', String(trace.span_count))}
          </span>
        ) : null}

        {/* Spacer */}
        <span className="flex-1" />

        {/* Compact metrics */}
        <span className="flex items-center gap-2 text-gray-500 font-mono flex-shrink-0">
          {trace.duration != null && <span>{formatDuration(trace.duration)}</span>}
          {trace.usage?.total_tokens != null && <span>{formatTokens(trace.usage.total_tokens)}</span>}
          {trace.total_estimated_cost != null && (
            <span>${trace.total_estimated_cost.toFixed(2)}</span>
          )}
        </span>
      </div>
    </div>
  );
}
